/**
 * Document API Hooks
 * Refactored: Phase 1 - Factory Pattern
 * Pattern: Uses createQueryHook and createMutationHook factories for code reduction
 */

import {
  Document,
  DocumentCreate,
  AccessCheckResult,
  BodyUpdateDocumentPermissionsApiDocumentsDocumentIdPermissionsPut,
} from '../../api/data-contracts';
import { log } from '../../utils/errors/logger';
import { QUERY_STALE_TIME } from '../../constants';
import { createQueryHook, createMutationHook } from './factories';

// --- Types ---

// Using the API-generated type for permissions updates
type DocumentPermissionsUpdate = BodyUpdateDocumentPermissionsApiDocumentsDocumentIdPermissionsPut;

interface UploadDocumentParams {
  file: File;
  title?: string;
  aclSettings?: Record<string, unknown>;
  textExtractionProvider?: string;
}

interface UpdateDocumentFileParams {
  documentId: string;
  file: File;
  title?: string;
  aclSettings?: Record<string, unknown>;
  textExtractionProvider?: string;
}

interface SuccessMessage {
  message: string;
}

interface DocumentsParams {
  skip?: number;
  limit?: number;
  search?: string;
  category?: string;
  source_type?: string;
  visibility?: string;
  owner_id?: string;
  sort_by?: string;
  created_at_gte?: string;
  created_at_lte?: string;
  updated_at_gte?: string;
  updated_at_lte?: string;
  shared_with_group?: string;
  shared_with_user?: string;
  exclude_owned?: boolean;
  organizationId?: string;
}

// --- Query Hooks ---

/**
 * Fetches documents list with filters and pagination.
 * Automatically organization-scoped. Superadmins can specify organizationId.
 * Returns paginated response with documents array and total count.
 */
export const useDocuments = createQueryHook<{ documents: Document[], total: number }, DocumentsParams>({
  queryKey: (params) => ['documents', params] as const,
  queryFn: (apiClient, params) => {
    const { organizationId, ...queryParams } = params;

    const headers: Record<string, string> = {};
    if (organizationId) {
      headers['X-Organization-Id'] = organizationId;
    }

    return apiClient.getDocumentsApiDocumentsGet(
      queryParams,
      { headers }
    ).then(response => response.data);
  },
  logContext: 'GET /api/documents',
  staleTime: QUERY_STALE_TIME.SHORT,
  onSuccess: (data) => {
    log.api.response('GET', '/api/documents', 200, {
      dataLength: data?.documents?.length ?? 0,
      total: data?.total ?? 0,
      result: data
    });
  },
});

/**
 * Fetches a single document by ID.
 */
export const useDocument = createQueryHook<Document, string>({
  queryKey: (documentId) => ['documents', documentId] as const,
  queryFn: (apiClient, documentId) =>
    apiClient.getDocumentApiDocumentsDocumentIdGet(documentId).then(response => response.data),
  logContext: 'GET /api/documents/:id',
  staleTime: QUERY_STALE_TIME.LONG,
});

/**
 * Fetches available text extraction providers.
 */
export const useTextExtractionProviders = createQueryHook<string[], void>({
  queryKey: () => ['text-extraction', 'providers'] as const,
  queryFn: (apiClient) =>
    apiClient.getAvailableProvidersApiAdminTextExtractionConfigProvidersGet().then(response => response.data),
  logContext: 'GET /api/admin/text-extraction-config/providers',
  staleTime: QUERY_STALE_TIME.LONG,
});

// --- Mutation Hooks ---

/**
 * Creates a new document from text content.
 */
export const useCreateDocument = createMutationHook<Document, DocumentCreate>({
  mutationFn: (apiClient, documentData) =>
    apiClient.createDocumentApiDocumentsPost(documentData).then(response => response.data),
  invalidateQueries: [['documents']],
  logContext: 'POST /api/documents',
  logMethod: 'POST',
  onSuccess: () => {
    log.user.action('document_created', 'useDocuments');
  },
});

/**
 * Uploads a document file.
 */
export const useUploadDocument = createMutationHook<Document, UploadDocumentParams>({
  mutationFn: (apiClient, params) => {
    const { file, title, aclSettings, textExtractionProvider } = params;

    // Convert array fields to comma-separated strings for backend
    const processedAclSettings = aclSettings ? {
      ...aclSettings,
      shared_with_users: Array.isArray(aclSettings.shared_with_users)
        ? aclSettings.shared_with_users.join(',')
        : aclSettings.shared_with_users,
      shared_with_groups: Array.isArray(aclSettings.shared_with_groups)
        ? aclSettings.shared_with_groups.join(',')
        : aclSettings.shared_with_groups,
    } : undefined;

    const uploadData = {
      file,
      title: title ?? undefined,
      ...processedAclSettings,
      text_extraction_provider: textExtractionProvider
    };

    return apiClient.uploadDocumentApiDocumentsUploadPost(uploadData).then(response => response.data);
  },
  invalidateQueries: [['documents']],
  logContext: 'POST /api/documents/upload',
  logMethod: 'POST',
  onSuccess: () => {
    log.user.action('document_uploaded', 'useDocuments');
  },
});

/**
 * Updates an existing document.
 * Note: The API expects DocumentCreate type for updates
 */
export const useUpdateDocument = createMutationHook<
  Document,
  { documentId: string; data: DocumentCreate }
>({
  mutationFn: (apiClient, { documentId, data }) =>
    apiClient.updateDocumentApiDocumentsDocumentIdPut(documentId, data).then(response => response.data),
  invalidateQueries: [['documents']],
  logContext: 'PUT /api/documents/:id',
  logMethod: 'PUT',
  onSuccess: (data, variables) => {
    log.user.action('document_updated', 'useDocuments', { documentId: variables.documentId });
  },
});

/**
 * Updates a document file.
 */
export const useUpdateDocumentFile = createMutationHook<Document, UpdateDocumentFileParams>({
  mutationFn: (apiClient, params) => {
    const { documentId, file, title, aclSettings, textExtractionProvider } = params;

    // Convert array fields to comma-separated strings for backend
    const processedAclSettings = aclSettings ? {
      ...aclSettings,
      shared_with_users: Array.isArray(aclSettings.shared_with_users)
        ? aclSettings.shared_with_users.join(',')
        : aclSettings.shared_with_users,
      shared_with_groups: Array.isArray(aclSettings.shared_with_groups)
        ? aclSettings.shared_with_groups.join(',')
        : aclSettings.shared_with_groups,
    } : undefined;

    const uploadData = {
      file,
      title: title ?? undefined,
      ...processedAclSettings,
      text_extraction_provider: textExtractionProvider
    };

    return apiClient.updateDocumentFileApiDocumentsDocumentIdFilePut(documentId, uploadData).then(response => response.data);
  },
  invalidateQueries: [['documents']],
  logContext: 'PUT /api/documents/:id/file',
  logMethod: 'PUT',
  onSuccess: (data, variables) => {
    log.user.action('document_file_updated', 'useDocuments', { documentId: variables.documentId });
  },
});

/**
 * Deletes a document.
 */
export const useDeleteDocument = createMutationHook<SuccessMessage, string>({
  mutationFn: (apiClient, documentId) =>
    apiClient.deleteDocumentApiDocumentsDocumentIdDelete(documentId).then(response => response.data),
  invalidateQueries: [['documents']],
  removeQueries: [],
  logContext: 'DELETE /api/documents/:id',
  logMethod: 'DELETE',
  onSuccess: (_data, documentId) => {
    log.user.action('document_deleted', 'useDocuments', { documentId });
  },
});

/**
 * Updates document permissions.
 */
export const useUpdateDocumentPermissions = createMutationHook<
  Document,
  { documentId: string; permissions: DocumentPermissionsUpdate }
>({
  mutationFn: (apiClient, { documentId, permissions }) =>
    apiClient.updateDocumentPermissionsApiDocumentsDocumentIdPermissionsPut(documentId, permissions).then(response => response.data),
  invalidateQueries: [['documents']],
  logContext: 'PUT /api/documents/:id/permissions',
  logMethod: 'PUT',
  onSuccess: (_data, variables) => {
    log.user.action('document_permissions_updated', 'useDocuments', { documentId: variables.documentId });
  },
});
